import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { useBlocker } from "react-router";
import { DiscardDialog } from "@/components/modal";

/**
 * Asks before leaving a form once anything in it has changed (decided with
 * Tiago 20 October): New event and Log a workout, whose parts wait on the
 * page until the form is sent. Anything different from how the form opened
 * counts — a box, a choice, a part kept on it — read as somebody leaves
 * rather than followed as they type, so a segmented control or a part's
 * hidden fields count as much as a title. Sending it is not leaving: the post
 * goes to the page's own address, and the redirect after it is the router's,
 * which no blocker stops.
 *
 * Asked in the app's own dialog, never the browser's `confirm`. Closing the
 * tab or reloading is the one exception: there a browser shows only its own
 * warning.
 */
export function LeaveGuard({
  form,
  title,
  children,
}: {
  form: RefObject<HTMLFormElement | null>;
  /** The question: "Leave without creating the event?" */
  title: string;
  /** What leaving loses. */
  children: ReactNode;
}) {
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (form.current) opened.current = snapshot(form.current);
  }, [form]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname && changed(form.current, opened.current),
  );

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (changed(form.current, opened.current)) event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [form]);

  if (blocker.state !== "blocked") return null;
  return (
    <DiscardDialog title={title} confirm="Leave" onConfirm={() => blocker.proceed()} onClose={() => blocker.reset()}>
      {children}
    </DiscardDialog>
  );
}

/** Whether the form says something other than it did as it opened. */
function changed(form: HTMLFormElement | null, opened: string | null): boolean {
  return form !== null && opened !== null && snapshot(form) !== opened;
}

/** What a form would send, as one string: a file by its name. */
function snapshot(form: HTMLFormElement): string {
  return JSON.stringify(
    [...new FormData(form).entries()].map(([name, value]) => [name, typeof value === "string" ? value : value.name]),
  );
}
